import { tracker } from '@openreplay/tracker';

/**
 * OpenReplay session replay (self-hosted at openreplay.7edge.io).
 *
 * Complements CloudWatch RUM (see ./rum): RUM gives the numbers, OpenReplay
 * gives the replay of the session that produced them.
 *
 * Disabled on production until PII masking is signed off — see the sanitizer
 * options below.
 */
const PROJECT_KEY = import.meta.env.VITE_OPENREPLAY_PROJECT_KEY;
const INGEST_POINT = import.meta.env.VITE_OPENREPLAY_INGEST;

const enabled = Boolean(PROJECT_KEY) && import.meta.env.VITE_STAGE !== 'pd';

if (enabled) try {
  tracker.configure({
    projectKey: PROJECT_KEY,
    ingestPoint: INGEST_POINT,

    // The tracker refuses to record on non-SSL origins. Only bypass that on the
    // local vite dev server (http://localhost:5173), never on a deployed build.
    __DISABLE_SECURE_MODE: import.meta.env.DEV,

    // ── Privacy ───────────────────────────────────────────────────────────
    // This is an admin portal over merchant/customer data, so mask by default.
    // Mark anything that still leaks with data-openreplay-obscured (masked)
    // or data-openreplay-hidden (removed from the replay entirely).
    defaultInputMode: 1,      // 0 Plain | 1 Obscured | 2 Hidden — all inputs masked
    obscureInputNumbers: true,
    obscureInputEmails: true,
    obscureInputDates: true,
    obscureTextNumbers: true, // amounts, card/phone numbers rendered as text
    obscureTextEmails: true,

    network: {
      // URL, method, status and timing are always captured — enough to debug
      // "Bad Requests". Bodies stay off until PII review clears them.
      capturePayload: false,
      failuresOnly: false,
      captureInIframes: false,
      sessionTokenHeader: false,
      ignoreHeaders: ['Authorization', 'Cookie', 'Set-Cookie', 'X-Amz-Security-Token'],
    },
  });

  void tracker.start();
} catch (error) {
  // Never let session replay break the app
  console.warn('OpenReplay failed to start', error);
}

/** Call after login so sessions are searchable by admin user. */
export const identifyOpenReplayUser = (email: string) => {
  if (!enabled || !email) return;
  try {
    tracker.setUserID(email);
  } catch {
    /* ignore */
  }
};

/** Replay link for the current session — paste into a Jira bug. */
export const getOpenReplaySessionURL = (): string | undefined => {
  if (!enabled) return undefined;
  try {
    return tracker.getSessionURL({ withCurrentTime: true });
  } catch {
    return undefined;
  }
};
